import { useEffect, useRef } from 'react';
import type { FC } from 'react';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, CheckSquare, Mic, GraduationCap, Newspaper } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCapture } from '@/contexts/CaptureContext';
import { useAuth } from '@/contexts/AuthContext';
import { hasManagerAccess } from '@/api/user';
import { useAccountConfig } from '@/hooks/useAccountConfig';
import { useNewsfeedUnreadCountQuery } from '@/pages/newsfeed/Newsfeed.api';
import { usePendingConfirmations } from '@/pages/newsfeed/MustReads.api';
import { useMustReadsEnabled } from '@/pages/newsfeed/hooks/useMustReadsEnabled';
import { CaptureDrawer } from './CaptureDrawer';
import { AnalyticsEventName } from '@/analytics/analytics.events';
import { track } from '@/analytics/track';

export const BottomNav: FC = () => {
  const { t } = useTranslation('shared-components');
  const { isCaptureOpen, setIsCaptureOpen } = useCapture();
  const { user } = useAuth();
  const { isFeatureEnabled } = useAccountConfig();
  const hasManagerTools = hasManagerAccess(user);
  const showNewsfeed = hasManagerTools && isFeatureEnabled('newsfeed');
  const showMic = hasManagerTools && isFeatureEnabled('recordings');
  const showTasks = isFeatureEnabled('tasks');
  const showAcademy = isFeatureEnabled('academy');

  const { data: unreadNewsfeedCount } = useNewsfeedUnreadCountQuery({ enabled: showNewsfeed });
  // The API leaves pending must-reads out of the unread count, so adding them counts none twice.
  const pendingConfirmationsCount = usePendingConfirmations({ enabled: showNewsfeed }).length;
  const unreadCount = (unreadNewsfeedCount ?? 0) + pendingConfirmationsCount;
  const isMustReadsEnabled = useMustReadsEnabled();
  const unreadCountLabelKey = isMustReadsEnabled
    ? 'bottom-nav.aria.unread-or-confirm-count'
    : 'bottom-nav.aria.unread-count';

  const navRef = useRef<HTMLElement>(null);

  // Publish the real nav height (padding + safe-area inset included) so full-screen
  // pages such as the embedded Academy can reserve exactly the space it takes.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const publishHeight = () => {
      document.documentElement.style.setProperty('--bottom-nav-height', `${nav.offsetHeight}px`);
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(nav);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--bottom-nav-height');
    };
  }, []);

  const handleOpenCapture = () => {
    setIsCaptureOpen(true);
  };

  const handleCloseCapture = () => {
    setIsCaptureOpen(false);
  };

  const handleAcademyClick = () => {
    track(AnalyticsEventName.ACADEMY_OPENED, { source: 'bottom_nav' });
  };

  return (
    <>
      <CaptureDrawer
        open={isCaptureOpen}
        onClose={handleCloseCapture}
      />

      <nav
        ref={navRef}
        className='fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/90 backdrop-blur-lg'
        aria-label={t('bottom-nav.aria.main-navigation')}
      >
        <div className='mx-auto flex max-w-md items-end justify-around py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]'>
          <RouterNavLink
            to='/'
            end
            state={{ arrivalSource: 'home_button' }}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-0.5 rounded-xl px-3 py-1.5 transition-colors',
                isActive ? 'text-primary-readable' : 'text-muted-foreground',
              )
            }
          >
            <Home
              className='h-5 w-5'
              aria-hidden
            />
            <span className='text-[10px] font-medium'>{t('bottom-nav.home')}</span>
          </RouterNavLink>

          {showNewsfeed && (
            <RouterNavLink
              to='/community/newsfeed'
              state={{ arrivalSource: 'bottom_nav' }}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 rounded-xl px-3 py-1.5 transition-colors',
                  isActive ? 'text-primary-readable' : 'text-muted-foreground',
                )
              }
            >
              <span className='relative'>
                <Newspaper
                  className='h-5 w-5'
                  aria-hidden
                />
                {unreadCount > 0 && (
                  <span className='absolute -right-1.5 -top-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground'>
                    <span className='sr-only'>{t(unreadCountLabelKey, { count: unreadCount })}</span>
                    <span aria-hidden>{unreadCount > 99 ? '99+' : unreadCount}</span>
                  </span>
                )}
              </span>
              <span className='text-[10px] font-medium'>{t('bottom-nav.newsfeed')}</span>
            </RouterNavLink>
          )}

          {showMic && (
            <button
              type='button'
              onClick={handleOpenCapture}
              className='relative -mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform active:scale-95'
              aria-label={t('bottom-nav.aria.open-check-in')}
            >
              <Mic
                className='h-7 w-7'
                aria-hidden
              />
            </button>
          )}

          {showTasks && (
            <RouterNavLink
              to='/tasks'
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 rounded-xl px-3 py-1.5 transition-colors',
                  isActive ? 'text-primary-readable' : 'text-muted-foreground',
                )
              }
            >
              <CheckSquare
                className='h-5 w-5'
                aria-hidden
              />
              <span className='text-[10px] font-medium'>{t('bottom-nav.tasks')}</span>
            </RouterNavLink>
          )}

          {showAcademy && (
            <RouterNavLink
              to='/academy'
              onClick={handleAcademyClick}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 rounded-xl px-3 py-1.5 transition-colors',
                  isActive ? 'text-primary-readable' : 'text-muted-foreground',
                )
              }
            >
              <GraduationCap
                className='h-5 w-5'
                aria-hidden
              />
              <span className='text-[10px] font-medium'>{t('bottom-nav.academy')}</span>
            </RouterNavLink>
          )}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
